import { FormContextProvider, FormSchemaFields } from '@stormwater-software/lib-form';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import { Modal } from '@stormwater-software/lib-ui';
import React, { useState } from 'react';
import { createOfflineSiteMapSource } from '../../../print/mapsPrint/siteMapPrintData';
import { useSiteMapPrint } from '../../../print/mapsPrint/useSiteMapPrint';

export interface IOfflineSiteMapPrintModalProps {
  onClose: () => void;
  targetDocs?: any[];
  projectId: number;
  projectName: string;
  clientName?: string;
  inspectionId?: number | null;
}

/** Defaults for every option key; stable identity keeps FormContext from re-seeding each render. */
const INITIAL_OPTIONS: Record<string, any> = { include_esri_layers: false };

/** Same shape as the online print's Map Options — add a key here for each new toggle. */
const getPrintOptionsSchema = (hasEsri: boolean) => ({
  map_options: {
    label: 'Map Options',
    controlType: UIControlType.plainText,
    style: { marginBottom: 0 },
  },
  ...(hasEsri && {
    include_esri_layers: {
      label: 'Include ESRI Layers',
      controlType: UIControlType.checkbox,
      style: {},
    },
  }),
});

/** Offline stand-in for the print job: renders the site maps locally and writes the PDF on-device. */
const OfflineSiteMapPrintModal: React.FC<IOfflineSiteMapPrintModalProps> = ({
  onClose,
  targetDocs,
  projectId,
  projectName,
  clientName,
  inspectionId,
}) => {
  const [options, setOptions] = useState<Record<string, any>>(INITIAL_OPTIONS);
  const print = useSiteMapPrint({
    source: createOfflineSiteMapSource({ projectId, docs: targetDocs, inspectionId }),
    projectName,
    clientName,
    emptyResultMessage: `None of these site maps are available offline.`,
    includeEsri: !!options.include_esri_layers,
    onComplete: onClose,
  });
  const hasEsri = !!print.maps?.some((map) => map.hasEsri);

  return (
    <Modal
      show={true}
      title='Print Site Maps'
      handleClose={onClose}
      handleSubmit={print.start}
      submitBtnText={print.printing ? `Preparing ${print.index + 1} of ${print.total}…` : 'Print'}
      disableSubmit={print.printing || !!print.error || !print.total}
      isSmall={true}
    >
      {print.error ? (
        <p className='error'>{print.error}</p>
      ) : !print.maps ? (
        <p>Looking for site maps saved on this device…</p>
      ) : !print.total ? (
        <p className='error'>No site maps are saved on this device for this project.</p>
      ) : (
        <p>
          You are offline, so{' '}
          {print.total > 1 ? `these ${print.total} site maps are` : 'this site map is'} printed on
          this device. Legend items, notes and findings placed on the map are included; feature
          label tags are not.
        </p>
      )}

      <div className='print-map-list'>
        {hasEsri && !print.error && (
          <FormContextProvider initialValue={INITIAL_OPTIONS}>
            <FormSchemaFields
              schema={getPrintOptionsSchema(hasEsri)}
              formData={options}
              onChange={(key, value) => setOptions((prev) => ({ ...prev, [key]: value }))}
            />
          </FormContextProvider>
        )}
      </div>

      {print.stage}
    </Modal>
  );
};

export default OfflineSiteMapPrintModal;
